import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Plus, Trash2, X, Search, Download, Upload, FileSpreadsheet,
  CheckCircle2, XCircle, Circle, ChevronRight, FileText, Archive,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import useEscapeKey from '../../hooks/useEscapeKey';
import {
  Button, Badge, Card, Modal, ConfirmDialog, Input, Select, Textarea,
  Table, THead, TBody, TR, TH, TD, LoadingState, EmptyState, Tabs, Spinner,
} from '../../components/ui';

// Searchable checkbox picker for assigning users
const UserPicker = ({ label, allUsers, selected, onChange, color = 'theme', role }) => {
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!search.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await api.get(`/users?role=${role}&search=${search}&limit=50`);
        setSearchResults(res.data.data?.users || []);
      } catch (err) {
        console.error('Search failed', err);
      } finally {
        setIsSearching(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [search, role]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const localMatches = allUsers.filter(u =>
      u.name?.toLowerCase().includes(q) ||
      u.username?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.rollNumber?.toLowerCase().includes(q)
    );
    const combined = [...localMatches];
    searchResults.forEach(su => {
      const id = su._id || su.id;
      if (!combined.find(u => (u._id || u.id) === id)) {
        combined.push(su);
      }
    });
    return combined;
  }, [allUsers, search, searchResults]);

  const allFilteredIds = filtered.map(u => u._id || u.id);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selected.includes(id));

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
  };

  const toggleAll = () => {
    if (allSelected) {
      onChange(selected.filter(id => !allFilteredIds.includes(id)));
    } else {
      const next = new Set([...selected, ...allFilteredIds]);
      onChange(Array.from(next));
    }
  };

  const colorMap = {
    theme: { chip: 'bg-themePrimary/10 text-themePrimary', check: 'accent-themePrimary', row: 'bg-themePrimary/5' },
    purple: { chip: 'bg-purple-100 text-purple-700', check: 'accent-purple-600', row: 'bg-purple-50' },
  };
  const c = colorMap[color] || colorMap.theme;

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map(id => {
            const u = filtered.find(x => (x._id || x.id) === id) || allUsers.find(x => (x._id || x.id) === id);
            if (!u) return null;
            return (
              <span key={id} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${c.chip}`}>
                {u.name || u.username}
                <button type="button" onClick={() => toggle(id)} className="hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
          <button type="button" onClick={() => onChange([])} className="text-xs text-slate-400 hover:text-red-500 underline">
            Clear all
          </button>
        </div>
      )}
      <div className="relative mb-1.5">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          {isSearching ? <Spinner size="sm" className="text-themePrimary" /> : <Search className="w-3.5 h-3.5 text-slate-400" />}
        </div>
        <input
          type="text"
          placeholder={`Search ${label.toLowerCase()}...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-themePrimary"
        />
      </div>
      {filtered.length > 0 && (
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1 px-1">
          <span>{filtered.length} user{filtered.length !== 1 ? 's' : ''} shown</span>
          <button type="button" onClick={toggleAll} className="text-themePrimary hover:underline font-medium">
            {allSelected ? 'Deselect all shown' : 'Select all shown'}
          </button>
        </div>
      )}
      <div className="border border-slate-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-4 text-sm text-slate-400 text-center">
            {isSearching ? 'Searching...' : 'No users found'}
          </div>
        ) : (
          filtered.map(u => {
            const id = u._id || u.id;
            const isSelected = selected.includes(id);
            return (
              <label
                key={id}
                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors ${isSelected ? c.row : ''}`}
              >
                <input type="checkbox" checked={isSelected} onChange={() => toggle(id)} className={`w-4 h-4 flex-shrink-0 ${c.check}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                  <p className="text-xs text-slate-500 truncate">{u.username}{u.rollNumber ? ` · ${u.rollNumber}` : ''}{u.email ? ` · ${u.email}` : ''}</p>
                </div>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

// SINGLE_CHOICE is identical to MCQ (single-answer multiple choice) — kept in model for
// backwards compatibility but removed from the creation UI to avoid confusion.
const QUESTION_TYPES = ['MCQ', 'TRUE_FALSE', 'DESCRIPTIVE', 'MULTIPLE_SELECT'];

const defaultQuestion = {
  text: '',
  type: 'MCQ',
  marks: 1,
  options: [
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false }
  ],
  correctAnswer: ''
};

const TABS = [
  { key: 'questions', label: 'Questions' },
  { key: 'assign', label: 'Assign' },
  { key: 'attempts', label: 'Attempts' },
  { key: 'results', label: 'Results' },
  { key: 'analytics', label: 'Analytics' },
];

const ExamDetail = () => {
  const { id } = useParams();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('questions');
  const [questions, setQuestions] = useState([]);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [questionForm, setQuestionForm] = useState(defaultQuestion);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [allProctors, setAllProctors] = useState([]);
  const [assignStudents, setAssignStudents] = useState([]);
  const [assignProctors, setAssignProctors] = useState([]);
  const [examStudents, setExamStudents] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [evalOverrides, setEvalOverrides] = useState({});
  const [evalSaving, setEvalSaving] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [examAnalytics, setExamAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [deleteQuestionId, setDeleteQuestionId] = useState(null);
  const bulkFileRef = useRef(null);

  const fetchExam = async () => {
    try {
      const [examRes, questionsRes, studentsRes] = await Promise.all([
        api.get(`/exams/${id}`),
        api.get(`/questions?examId=${id}`),
        api.get(`/exams/${id}/students`).catch(() => ({ data: { data: [] } }))
      ]);
      const data = examRes.data.data;
      setExam(data);
      setQuestions(questionsRes.data.data || []);
      setExamStudents(studentsRes.data.data || []);
      setAssignStudents((data.assignedStudents || []).map(s => s._id || s.id || s));
      setAssignProctors((data.assignedProctors || []).map(p => p._id || p.id || p));
    } catch (err) {
      toast.error('Failed to load exam details');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const [studRes, proctRes] = await Promise.all([
        api.get('/users?role=STUDENT&limit=1000'),
        api.get('/users?role=PROCTOR&limit=300')
      ]);
      setAllStudents(studRes.data.data?.users || []);
      setAllProctors(proctRes.data.data?.users || []);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    fetchExam();
    fetchUsers();
  }, [id]);

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    if (!questionForm.text.trim()) {
      toast.error('Question text is required');
      return;
    }
    setQuestionLoading(true);
    try {
      await api.post(`/questions`, { ...questionForm, exam: id });
      toast.success('Question added');
      setShowQuestionForm(false);
      setQuestionForm(defaultQuestion);
      fetchExam();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add question');
    } finally {
      setQuestionLoading(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!deleteQuestionId) return;
    try {
      await api.delete(`/questions/${deleteQuestionId}`);
      toast.success('Question deleted');
      setDeleteQuestionId(null);
      fetchExam();
    } catch (err) {
      toast.error('Failed to delete question');
    }
  };

  const handleAssign = async () => {
    setAssignLoading(true);
    try {
      await api.put(`/exams/${id}`, {
        assignedStudents: assignStudents,
        assignedProctors: assignProctors,
      });
      toast.success('Assignments saved');
      fetchExam();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save assignments');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleEvaluate = async (studentId) => {
    try {
      const res = await api.get(`/exams/${id}/attempts/${studentId}`);
      setSelectedAttempt(res.data.data);
      setEvalOverrides({});
    } catch (err) {
      toast.error('Failed to load attempt details');
    }
  };

  const fetchResults = async () => {
    setResultsLoading(true);
    try {
      const res = await api.get(`/results/${id}`);
      setExamResults(res.data.data?.attempts || []);
    } catch (err) {
      toast.error('Failed to load results');
    } finally {
      setResultsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'results') fetchResults();
  }, [activeTab]); // eslint-disable-line

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api.get(`/results/${id}/analytics`);
      setExamAnalytics(res.data.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'analytics') fetchAnalytics();
  }, [activeTab]); // eslint-disable-line

  const handleDownloadAllResults = async () => {
    try {
      const res = await api.get(`/results/${id}/download-all`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = (exam?.name || 'exam').replace(/[^a-z0-9]/gi, '_') + '_all_results.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download results');
    }
  };

  const handleDownloadStudentResult = async (studentId, studentName) => {
    try {
      const res = await api.get(`/results/${id}/download/${studentId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = (studentName || 'student').replace(/[^a-z0-9]/gi, '_') + '_result.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download result');
    }
  };

  const [downloadingScoreCards, setDownloadingScoreCards] = useState(false);
  const [downloadingScoreCardId, setDownloadingScoreCardId] = useState(null);

  const handleDownloadScoreCard = async (studentId, studentName) => {
    setDownloadingScoreCardId(studentId);
    try {
      const res = await api.get(`/results/${id}/scorecard/${studentId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Score_Card_${(studentName || 'student').replace(/[^a-z0-9]/gi, '_')}_${(exam?.name || 'exam').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download score card');
    } finally {
      setDownloadingScoreCardId(null);
    }
  };

  const handleDownloadScoreCardsBulk = async () => {
    setDownloadingScoreCards(true);
    try {
      const res = await api.get(`/results/${id}/scorecards-bulk`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = (exam?.name || 'exam').replace(/[^a-z0-9]/gi, '_') + '_score_cards.zip';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download score cards');
    } finally {
      setDownloadingScoreCards(false);
    }
  };

  const handleSaveEvaluation = async () => {
    if (!selectedAttempt || Object.keys(evalOverrides).length === 0) return;
    setEvalSaving(true);
    try {
      const overrides = Object.entries(evalOverrides).map(([idx, ov]) => ({
        questionId: selectedAttempt.answers[parseInt(idx)].question._id,
        isCorrect: ov.isCorrect,
        marksObtained: ov.marksObtained,
      }));
      const res = await api.put(`/exams/${id}/attempts/${selectedAttempt.student._id}/evaluate`, { overrides });
      setSelectedAttempt(res.data.data);
      setEvalOverrides({});
      toast.success('Evaluation saved');
      fetchExam();
    } catch (err) {
      toast.error('Failed to save evaluation');
    } finally {
      setEvalSaving(false);
    }
  };

  const handleDownloadQuestionSample = async () => {
    try {
      const res = await api.get('/questions/sample-sheet', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'questions_sample.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download sample sheet');
    }
  };

  const handleBulkUploadQuestions = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('examId', id);

    setBulkLoading(true);
    const uploadPromise = api.post('/questions/bulk-upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    toast.promise(uploadPromise, {
      loading: 'Uploading and processing questions...',
      success: (res) => {
        const { created, errors } = res.data.data || {};
        fetchExam();
        return `${created || 0} questions uploaded${errors ? `, ${errors} failed` : ''}`;
      },
      error: (err) => err.response?.data?.message || 'Bulk upload failed'
    });

    try {
      await uploadPromise;
    } catch {
      // toast.promise handles it
    } finally {
      setBulkLoading(false);
      e.target.value = '';
    }
  };

  const handleOptionChange = (idx, field, value) => {
    const opts = [...questionForm.options];
    if (field === 'isCorrect') {
      if (questionForm.type === 'MCQ' || questionForm.type === 'SINGLE_CHOICE' || questionForm.type === 'TRUE_FALSE') {
        opts.forEach((o, i) => { opts[i] = { ...opts[i], isCorrect: i === idx }; });
        if (questionForm.type === 'TRUE_FALSE') {
          const correctAnswer = opts[idx].text.toLowerCase();
          setQuestionForm(p => ({ ...p, options: opts, correctAnswer }));
          return;
        }
      } else {
        opts[idx] = { ...opts[idx], isCorrect: value };
      }
    } else {
      opts[idx] = { ...opts[idx], [field]: value };
    }
    setQuestionForm(p => ({ ...p, options: opts }));
  };

  const addOption = () => {
    setQuestionForm(p => ({ ...p, options: [...p.options, { text: '', isCorrect: false }] }));
  };

  const removeOption = (idx) => {
    if (questionForm.options.length <= 2) return;
    setQuestionForm(p => ({ ...p, options: p.options.filter((_, i) => i !== idx) }));
  };

  const needsOptions = ['MCQ', 'SINGLE_CHOICE', 'MULTIPLE_SELECT', 'TRUE_FALSE'].includes(questionForm.type);

  // ESC closes the topmost open popup (evaluation modal first, then question form)
  const handleEscape = useCallback(() => {
    if (selectedAttempt) { setSelectedAttempt(null); setEvalOverrides({}); return; }
    if (showQuestionForm) { setShowQuestionForm(false); setQuestionForm(defaultQuestion); }
  }, [selectedAttempt, showQuestionForm]);

  useEscapeKey(handleEscape, !!(selectedAttempt || showQuestionForm));

  if (loading) return <LoadingState label="Loading exam details..." />;

  if (!exam) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Exam not found.</p>
        <Link to="/admin/exams" className="text-themePrimary hover:underline text-sm mt-2 inline-block">
          Back to Exams
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb + Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
          <Link to="/admin/exams" className="hover:text-themePrimary">Exams</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-slate-800 font-medium">{exam.name}</span>
        </div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{exam.name}</h2>
            <p className="text-slate-500 text-sm mt-1">{exam.duration} minutes</p>
          </div>
          <Badge>{exam.status}</Badge>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Questions', value: questions.length },
          { label: 'Duration', value: `${exam.duration} min` },
          { label: 'Warning Limit', value: exam.warningLimit || 3 },
          { label: 'Status', value: exam.status }
        ].map(({ label, value }) => (
          <Card key={label} padding="p-4" className="text-center">
            <p className="text-xl font-bold text-slate-900">{value}</p>
            <p className="text-xs text-slate-500 mt-1">{label}</p>
          </Card>
        ))}
      </div>

      <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {/* Questions Tab */}
      {activeTab === 'questions' && (
        <div className="space-y-4">
          <input
            ref={bulkFileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleBulkUploadQuestions}
          />
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-slate-600">{questions.length} question(s)</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="secondary" icon={FileSpreadsheet} onClick={handleDownloadQuestionSample} title="Download sample xlsx — fill it and upload">
                Sample Sheet
              </Button>
              <Button variant="secondary" icon={Upload} loading={bulkLoading} onClick={() => bulkFileRef.current?.click()}>
                {bulkLoading ? 'Uploading...' : 'Bulk Upload'}
              </Button>
              <Button variant="primary" icon={Plus} onClick={() => setShowQuestionForm(!showQuestionForm)}>
                Add Question
              </Button>
            </div>
          </div>

          {/* Add Question Form */}
          {showQuestionForm && (
            <Card>
              <h4 className="text-base font-semibold text-slate-800 mb-4">New Question</h4>
              <form onSubmit={handleAddQuestion} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <Textarea
                      label="Question Text *"
                      value={questionForm.text}
                      onChange={e => setQuestionForm(p => ({ ...p, text: e.target.value }))}
                      rows={3}
                      placeholder="Enter question text..."
                      required
                    />
                  </div>
                  <div className="space-y-3">
                    <Select
                      label="Type"
                      value={questionForm.type}
                      onChange={e => {
                        const newType = e.target.value;
                        if (newType === 'TRUE_FALSE') {
                          setQuestionForm(p => ({ ...p, type: newType, options: [{ text: 'True', isCorrect: false }, { text: 'False', isCorrect: false }], correctAnswer: '' }));
                        } else if (questionForm.type === 'TRUE_FALSE') {
                          setQuestionForm(p => ({ ...p, type: newType, options: defaultQuestion.options, correctAnswer: '' }));
                        } else {
                          setQuestionForm(p => ({ ...p, type: newType }));
                        }
                      }}
                    >
                      {QUESTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </Select>
                    <Input
                      label="Marks"
                      type="number"
                      value={questionForm.marks}
                      onChange={e => setQuestionForm(p => ({ ...p, marks: parseInt(e.target.value) || 1 }))}
                      min={1}
                    />
                  </div>
                </div>

                {needsOptions && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-700">
                        Options {questionForm.type === 'TRUE_FALSE' ? '(True/False)' : ''}
                      </label>
                      {questionForm.type !== 'TRUE_FALSE' && (
                        <button type="button" onClick={addOption} className="text-xs text-themePrimary hover:underline">
                          + Add Option
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {questionForm.options.map((opt, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <input
                            type={['MCQ', 'SINGLE_CHOICE', 'TRUE_FALSE'].includes(questionForm.type) ? 'radio' : 'checkbox'}
                            checked={opt.isCorrect}
                            onChange={e => handleOptionChange(idx, 'isCorrect', e.target.checked)}
                            className="flex-shrink-0 accent-themePrimary"
                            title="Mark as correct answer"
                          />
                          {questionForm.type === 'TRUE_FALSE' ? (
                            <span className="text-sm text-slate-700 flex-1">{opt.text}</span>
                          ) : (
                            <input
                              type="text"
                              value={opt.text}
                              onChange={e => handleOptionChange(idx, 'text', e.target.value)}
                              placeholder={`Option ${idx + 1}`}
                              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-themePrimary"
                            />
                          )}
                          {questionForm.type !== 'TRUE_FALSE' && questionForm.options.length > 2 && (
                            <button type="button" onClick={() => removeOption(idx)} className="text-red-400 hover:text-red-600">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      {['MCQ', 'SINGLE_CHOICE', 'TRUE_FALSE'].includes(questionForm.type)
                        ? 'Select the radio button for the correct answer'
                        : 'Check all correct answers'}
                    </p>
                  </div>
                )}

                {questionForm.type === 'DESCRIPTIVE' && (
                  <Textarea
                    label="Model Answer (optional)"
                    value={questionForm.correctAnswer}
                    onChange={e => setQuestionForm(p => ({ ...p, correctAnswer: e.target.value }))}
                    rows={2}
                    placeholder="Model answer for grading reference..."
                  />
                )}

                <div className="flex gap-3">
                  <Button variant="outline" fullWidth type="button" onClick={() => { setShowQuestionForm(false); setQuestionForm(defaultQuestion); }}>
                    Cancel
                  </Button>
                  <Button variant="primary" fullWidth type="submit" loading={questionLoading}>
                    Add Question
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Questions List */}
          {questions.length === 0 ? (
            <Card><EmptyState title="No questions yet" description="Add your first question to get started." /></Card>
          ) : (
            <div className="space-y-3">
              {questions.map((q, idx) => (
                <Card key={q._id || q.id || idx} padding="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold text-slate-500">Q{idx + 1}</span>
                        <Badge variant="brand">{q.type}</Badge>
                        <span className="text-xs text-slate-400">{q.marks} mark{q.marks !== 1 ? 's' : ''}</span>
                      </div>
                      <p className="text-sm text-slate-800">{q.text}</p>
                      {q.options && q.options.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {q.options.map((opt, oi) => (
                            <div key={oi} className={`flex items-center gap-2 text-xs ${opt.isCorrect ? 'text-green-700 font-medium' : 'text-slate-500'}`}>
                              {opt.isCorrect ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                              <span>{opt.text}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {q.correctAnswer && (
                        <div className="mt-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
                          <p className="text-xs font-semibold text-slate-700 mb-1">Correct / Model Answer:</p>
                          <p className="text-sm text-slate-600 whitespace-pre-wrap">{q.correctAnswer}</p>
                        </div>
                      )}
                    </div>
                    <button onClick={() => setDeleteQuestionId(q._id || q.id)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assign Tab */}
      {activeTab === 'assign' && (
        <div className="space-y-6">
          <Card className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-slate-800 mb-1">Students</h4>
              <p className="text-xs text-slate-500 mb-4">
                {(exam.assignedStudents || []).length} currently assigned. Search and check students to add them.
              </p>
              <UserPicker label="Select Students" allUsers={allStudents} selected={assignStudents} onChange={setAssignStudents} color="theme" role="STUDENT" />
            </div>

            <div className="border-t border-slate-100 pt-6">
              <h4 className="text-base font-semibold text-slate-800 mb-1">Proctors</h4>
              <p className="text-xs text-slate-500 mb-4">
                {(exam.assignedProctors || []).length} currently assigned. Search and check proctors to add them.
              </p>
              <UserPicker label="Select Proctors" allUsers={allProctors} selected={assignProctors} onChange={setAssignProctors} color="purple" role="PROCTOR" />
            </div>
          </Card>

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              variant="primary"
              loading={assignLoading}
              disabled={assignStudents.length === 0 && assignProctors.length === 0}
              onClick={handleAssign}
            >
              Save Assignments ({assignStudents.length + assignProctors.length} selected)
            </Button>
            {(assignStudents.length > 0 || assignProctors.length > 0) && (
              <p className="text-xs text-slate-500">
                {assignStudents.length} student{assignStudents.length !== 1 ? 's' : ''} · {assignProctors.length} proctor{assignProctors.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>
        </div>
      )}

      {/* Attempts Tab */}
      {activeTab === 'attempts' && (
        <Card padding="p-0">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h4 className="font-semibold text-slate-800">Student Attempts &amp; Evaluation</h4>
          </div>
          {examStudents.length === 0 ? (
            <EmptyState title="No students assigned" description="Assign students to this exam to see their attempts here." />
          ) : (
            <div className="divide-y divide-slate-100">
              {examStudents.map(({ student, attempt }) => (
                <div key={student._id || student.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 hover:bg-slate-50 transition-colors gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{student.name}</p>
                    <p className="text-xs text-slate-500 truncate max-w-[250px]">{student.email} • {student.username}</p>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-4">
                    <Badge variant={attempt ? undefined : 'neutral'}>{attempt ? attempt.status : 'NOT STARTED'}</Badge>
                    {(attempt && (attempt.status === 'SUBMITTED' || attempt.status === 'FORCE_SUBMITTED')) && (
                      <Button size="sm" variant="primary" onClick={() => handleEvaluate(student._id || student.id)}>
                        Evaluate
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-slate-600">
              {resultsLoading ? 'Loading...' : `${examResults.length} submitted result(s)`}
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" icon={Archive} loading={downloadingScoreCards} disabled={examResults.length === 0} onClick={handleDownloadScoreCardsBulk}>
                Download Score Cards (.zip)
              </Button>
              <Button variant="primary" icon={Download} disabled={examResults.length === 0} onClick={handleDownloadAllResults}>
                Download All Results (.xlsx)
              </Button>
            </div>
          </div>

          {resultsLoading ? (
            <LoadingState label="Loading results..." />
          ) : examResults.length === 0 ? (
            <Card><EmptyState title="No submitted results yet" /></Card>
          ) : (
            <Card padding="p-0">
              <Table>
                <THead>
                  <TR>
                    <TH>Student</TH>
                    <TH>Score</TH>
                    <TH>Percentage</TH>
                    <TH>Status</TH>
                    <TH>Submitted At</TH>
                    <TH>Download</TH>
                  </TR>
                </THead>
                <TBody>
                  {examResults.map((attempt) => {
                    const pct = attempt.totalMarks > 0 ? ((attempt.score / attempt.totalMarks) * 100).toFixed(1) + '%' : '—';
                    return (
                      <TR key={attempt._id}>
                        <TD className="min-w-[150px]">
                          <p className="font-medium text-slate-900">{attempt.student?.name}</p>
                          <p className="text-xs text-slate-500 truncate max-w-[200px]">{attempt.student?.email}</p>
                          {attempt.student?.rollNumber && <p className="text-xs text-slate-400">{attempt.student.rollNumber}</p>}
                        </TD>
                        <TD className="font-semibold text-slate-800 whitespace-nowrap">{attempt.score} / {attempt.totalMarks}</TD>
                        <TD className="whitespace-nowrap">
                          <span className={`font-semibold ${parseFloat(pct) >= 50 ? 'text-green-700' : 'text-red-600'}`}>{pct}</span>
                        </TD>
                        <TD><Badge variant="success">{attempt.status}</Badge></TD>
                        <TD className="text-xs whitespace-nowrap">{attempt.submittedAt ? new Date(attempt.submittedAt).toLocaleString() : '—'}</TD>
                        <TD>
                          <div className="flex gap-1 flex-wrap">
                            <Button size="sm" variant="ghost" icon={Download} onClick={() => handleDownloadStudentResult(attempt.student?._id || attempt.student?.id, attempt.student?.name)}>
                              Excel
                            </Button>
                            <Button
                              size="sm" variant="ghost" icon={FileText}
                              loading={downloadingScoreCardId === (attempt.student?._id || attempt.student?.id)}
                              onClick={() => handleDownloadScoreCard(attempt.student?._id || attempt.student?.id, attempt.student?.name)}
                            >
                              Score Card
                            </Button>
                          </div>
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </Card>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-5">
          {analyticsLoading ? (
            <LoadingState label="Loading analytics..." />
          ) : !examAnalytics || examAnalytics.totalAttempts === 0 ? (
            <Card><EmptyState title="No submitted attempts yet" subtitle="Analytics appear once students have submitted this exam." /></Card>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Card className="text-center py-4">
                  <p className="text-2xl font-bold text-slate-900">{examAnalytics.totalAttempts}</p>
                  <p className="text-xs text-slate-500 mt-1">Submitted attempts</p>
                </Card>
                <Card className="text-center py-4">
                  <p className="text-2xl font-bold text-slate-900">{examAnalytics.totalQuestions}</p>
                  <p className="text-xs text-slate-500 mt-1">Total questions</p>
                </Card>
              </div>

              {examAnalytics.bySection.length > 0 && (
                <Card padding="p-0">
                  <div className="px-4 pt-4 pb-2">
                    <h3 className="text-sm font-semibold text-slate-800">By section</h3>
                  </div>
                  <Table>
                    <THead>
                      <TR><TH>Section</TH><TH>Questions</TH><TH>Average accuracy</TH></TR>
                    </THead>
                    <TBody>
                      {examAnalytics.bySection.map((s) => (
                        <TR key={s.key}>
                          <TD>{s.key}</TD>
                          <TD>{s.questionCount}</TD>
                          <TD>
                            <span className={`font-semibold ${s.averageAccuracy >= 70 ? 'text-green-700' : s.averageAccuracy >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                              {s.averageAccuracy}%
                            </span>
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </Card>
              )}

              {examAnalytics.byDifficulty.length > 0 && (
                <Card padding="p-0">
                  <div className="px-4 pt-4 pb-2">
                    <h3 className="text-sm font-semibold text-slate-800">By difficulty</h3>
                  </div>
                  <Table>
                    <THead>
                      <TR><TH>Difficulty</TH><TH>Questions</TH><TH>Average accuracy</TH></TR>
                    </THead>
                    <TBody>
                      {examAnalytics.byDifficulty.map((d) => (
                        <TR key={d.key}>
                          <TD>{d.key}</TD>
                          <TD>{d.questionCount}</TD>
                          <TD>
                            <span className={`font-semibold ${d.averageAccuracy >= 70 ? 'text-green-700' : d.averageAccuracy >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                              {d.averageAccuracy}%
                            </span>
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </Card>
              )}

              <Card padding="p-0">
                <div className="px-4 pt-4 pb-2">
                  <h3 className="text-sm font-semibold text-slate-800">Per-question breakdown</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Questions students most often get wrong are the ones worth reviewing.</p>
                </div>
                <Table>
                  <THead>
                    <TR><TH>Question</TH><TH>Section</TH><TH>Difficulty</TH><TH>Attempted</TH><TH>Correct</TH><TH>Accuracy</TH></TR>
                  </THead>
                  <TBody>
                    {[...examAnalytics.questions]
                      .sort((a, b) => (a.accuracy ?? 101) - (b.accuracy ?? 101))
                      .map((q) => (
                        <TR key={q.questionId}>
                          <TD className="max-w-[320px] truncate" title={q.text}>{q.text}</TD>
                          <TD>{q.section}</TD>
                          <TD><Badge variant="neutral">{q.difficultyLevel}</Badge></TD>
                          <TD>{q.attempted}</TD>
                          <TD>{q.correct}</TD>
                          <TD>
                            {q.accuracy === null ? (
                              <span className="text-slate-400">{q.type === 'DESCRIPTIVE' ? 'Manual grading' : 'Not attempted'}</span>
                            ) : (
                              <span className={`font-semibold ${q.accuracy >= 70 ? 'text-green-700' : q.accuracy >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                                {q.accuracy}%
                              </span>
                            )}
                          </TD>
                        </TR>
                      ))}
                  </TBody>
                </Table>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Evaluation Modal */}
      <Modal
        open={!!selectedAttempt}
        onClose={() => { setSelectedAttempt(null); setEvalOverrides({}); }}
        size="xl"
        title={selectedAttempt ? `Review: ${selectedAttempt.student.name} (${selectedAttempt.student.username})` : ''}
        footer={
          <div className="flex items-center justify-between w-full">
            <p className="text-xs text-slate-500">
              {Object.keys(evalOverrides).length > 0
                ? `${Object.keys(evalOverrides).length} override(s) pending`
                : 'Click "Mark Correct / Incorrect" on any answer to override auto-evaluation'}
            </p>
            <Button variant="primary" loading={evalSaving} disabled={Object.keys(evalOverrides).length === 0} onClick={handleSaveEvaluation}>
              Save Evaluation
            </Button>
          </div>
        }
      >
        {selectedAttempt && (
          <div className="space-y-6">
            <p className="text-sm text-slate-500 -mt-2">Score: {selectedAttempt.score}/{selectedAttempt.totalMarks}</p>
            {selectedAttempt.answers?.map((ans, idx) => {
              const q = ans.question;
              if (!q) return null;
              const override = evalOverrides[idx];
              const displayCorrect = override !== undefined ? override.isCorrect : ans.isCorrect;
              const displayMarks = override !== undefined ? override.marksObtained : ans.marksObtained;
              const isDescriptive = q.type === 'DESCRIPTIVE';
              return (
                <div key={idx} className={`bg-slate-50 p-5 rounded-xl border ${override !== undefined ? 'border-amber-400' : 'border-slate-200'}`}>
                  <div className="flex justify-between items-start mb-4 gap-4">
                    <p className="text-sm font-semibold text-slate-800 flex-1">
                      <span className="text-themePrimary mr-2">Q{idx + 1}.</span> {q.text}
                    </p>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                      <Badge variant={displayCorrect === true ? 'success' : displayCorrect === false ? 'danger' : 'neutral'}>
                        {displayCorrect === true ? 'Correct' : displayCorrect === false ? 'Incorrect' : 'Pending'}
                      </Badge>
                      {isDescriptive && override !== undefined ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min={0}
                            max={q.marks}
                            value={displayMarks}
                            onChange={e => setEvalOverrides(p => ({ ...p, [idx]: { ...p[idx], marksObtained: Math.min(q.marks, Math.max(0, Number(e.target.value))) } }))}
                            className="w-14 border border-slate-300 rounded px-2 py-0.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-themePrimary"
                          />
                          <span className="text-xs text-slate-400">/ {q.marks}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500 font-medium">{displayMarks} / {q.marks} marks</span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {q.options && q.options.length > 0 ? (
                      <div className="border border-slate-200 rounded-lg p-3 bg-white">
                        <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Options</p>
                        <ul className="space-y-1.5 text-sm">
                          {q.options.map((opt, oi) => {
                            const isStudentAnswer = Array.isArray(ans.answer) ? ans.answer.includes(opt._id) || ans.answer.includes(String(oi)) : ans.answer === opt._id || ans.answer === String(oi) || ans.answer === opt.text;
                            return (
                              <li key={oi} className={`flex items-start gap-2 ${opt.isCorrect ? 'font-semibold text-green-700' : 'text-slate-600'}`}>
                                {opt.isCorrect ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <Circle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                                <span className={isStudentAnswer ? 'underline decoration-2 underline-offset-2' : ''}>{opt.text}</span>
                                {isStudentAnswer && <Badge variant="info">Selected</Badge>}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : (
                      <div className="border border-slate-200 rounded-lg p-3 bg-white text-sm">
                        <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Student's Answer</p>
                        <p className="text-slate-700 whitespace-pre-wrap text-xs md:text-sm">{ans.answer || <span className="text-slate-400 italic">No Answer Provided</span>}</p>
                      </div>
                    )}

                    <div className="border border-slate-200 rounded-lg p-3 bg-white text-sm">
                      <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Correct Answer</p>
                      <p className="text-green-700 font-medium whitespace-pre-wrap text-xs md:text-sm">{q.correctAnswer || <span className="text-slate-400 italic">Auto-evaluated</span>}</p>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-400 mr-1 w-full sm:w-auto">Override:</span>
                    <Button
                      size="sm"
                      variant={displayCorrect === true && override !== undefined ? 'primary' : 'outline'}
                      icon={CheckCircle2}
                      className={displayCorrect === true && override !== undefined ? '!bg-green-600 hover:!bg-green-700' : '!border-green-400 !text-green-700 hover:!bg-green-50'}
                      onClick={() => setEvalOverrides(p => ({ ...p, [idx]: { isCorrect: true, marksObtained: q.marks } }))}
                    >
                      Mark Correct
                    </Button>
                    <Button
                      size="sm"
                      variant={displayCorrect === false && override !== undefined ? 'danger' : 'outline'}
                      icon={XCircle}
                      className={displayCorrect !== false || override === undefined ? '!border-red-300 !text-red-600 hover:!bg-red-50' : ''}
                      onClick={() => setEvalOverrides(p => ({ ...p, [idx]: { isCorrect: false, marksObtained: 0 } }))}
                    >
                      Mark Incorrect
                    </Button>
                    {override !== undefined && (
                      <Button size="sm" variant="ghost" onClick={() => setEvalOverrides(p => { const n = { ...p }; delete n[idx]; return n; })}>
                        Reset
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}

            {!selectedAttempt.answers?.length && (
              <div className="text-center py-12 text-slate-500 text-sm">
                This student has not submitted any answers.
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleteQuestionId}
        onClose={() => setDeleteQuestionId(null)}
        onConfirm={handleDeleteQuestion}
        variant="danger"
        title="Delete this question?"
        description="This cannot be undone."
        confirmLabel="Delete"
      />
    </div>
  );
};

export default ExamDetail;
